import _ from "lodash";
import React from "react";
import { Button, FormControl } from "react-bootstrap";

import AfHelp from "./afHelp";
import ColorPicker from "./colorPicker";
import globals from "../../constants/env";
import {
	parseJsonStyleObject,
	stringifyJsonStyleObject
} from "../../utils/labelsStylesheetUtils";

const FONT_WEIGHT_OPTIONS = [
	{ value: "", label: "Inherit" },
	{ value: "normal", label: "Normal" },
	{ value: "bold", label: "Bold" },
	{ value: "500", label: "500" },
	{ value: "600", label: "600" },
	{ value: "700", label: "700" }
];

const TEXT_ALIGN_OPTIONS = [
	{ value: "", label: "Inherit" },
	{ value: "left", label: "Left" },
	{ value: "center", label: "Center" },
	{ value: "right", label: "Right" },
	{ value: "justify", label: "Justify" }
];

const TEXT_DECORATION_OPTIONS = [
	{ value: "", label: "Inherit" },
	{ value: "none", label: "None" },
	{ value: "underline", label: "Underline" }
];

const BORDER_STYLE_OPTIONS = [
	{ value: "", label: "Inherit" },
	{ value: "solid", label: "Solid" },
	{ value: "dashed", label: "Dashed" },
	{ value: "none", label: "None" }
];

const buildStylePaths = (styleKey = "", props = []) => props.map((prop) => [styleKey, prop]);

const MARKDOWN_STYLE_CLEAR_PATHS = [
	...buildStylePaths("text", ["color", "fontFamily", "fontSize", "fontWeight", "fontStyle", "lineHeight", "textAlign"]),
	...buildStylePaths("paragraph", ["color", "fontFamily", "fontSize", "fontWeight", "fontStyle", "lineHeight", "marginTop", "marginBottom", "textAlign"]),
	...buildStylePaths("link", ["color", "fontWeight", "textDecorationLine"]),
	...buildStylePaths("strong", ["color", "fontWeight"]),
	...buildStylePaths("heading", ["color", "fontFamily", "fontWeight", "marginTop", "marginBottom", "textAlign"]),
	...buildStylePaths("heading1", ["fontSize", "lineHeight", "color", "fontWeight"]),
	...buildStylePaths("heading2", ["fontSize", "lineHeight", "color", "fontWeight"]),
	...buildStylePaths("heading3", ["fontSize", "lineHeight", "color", "fontWeight"]),
	...buildStylePaths("blockquote", ["alignSelf", "backgroundColor", "borderLeftColor", "borderLeftWidth", "borderLeftStyle", "borderStyle", "padding", "paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "marginTop", "marginRight", "marginBottom", "marginLeft", "borderRadius", "color"]),
	...buildStylePaths("list", ["marginTop", "marginBottom", "paddingLeft"]),
	...buildStylePaths("listUnordered", ["marginTop", "marginBottom", "paddingLeft"]),
	...buildStylePaths("listOrdered", ["marginTop", "marginBottom", "paddingLeft"]),
	...buildStylePaths("listItem", ["marginBottom", "color", "fontSize", "lineHeight"]),
	...buildStylePaths("listUnorderedItemText", ["color", "fontSize", "lineHeight", "fontWeight"]),
	...buildStylePaths("listOrderedItemText", ["color", "fontSize", "lineHeight", "fontWeight"]),
	...buildStylePaths("listUnorderedItemIcon", ["color", "fontSize", "lineHeight", "fontWeight"]),
	...buildStylePaths("listOrderedItemIcon", ["color", "fontSize", "lineHeight", "fontWeight"]),
	...buildStylePaths("inlineCode", ["backgroundColor", "borderColor", "borderWidth", "borderRadius", "color", "fontFamily", "fontWeight", "padding"]),
	...buildStylePaths("codeInline", ["backgroundColor", "borderColor", "borderWidth", "borderRadius", "color", "fontFamily", "fontWeight", "padding"]),
	...buildStylePaths("codeBlock", ["backgroundColor", "borderColor", "borderWidth", "borderRadius", "color", "fontFamily", "fontSize", "padding"]),
	...buildStylePaths("table", ["borderColor", "borderWidth", "borderRadius"]),
	...buildStylePaths("tableHeaderCell", ["backgroundColor", "borderColor", "borderRightColor", "borderRightWidth", "color", "fontWeight", "padding"]),
	...buildStylePaths("tableRowCell", ["borderColor", "borderRightColor", "borderRightWidth", "color", "padding"])
];

const MARKDOWN_STYLE_TEMPLATE_PRESETS = [
	{
		key: "defaultCallouts",
		label: "Default Callouts",
		styles: {
			blockquote: {
				alignSelf: "stretch",
				backgroundColor: "#f6f8fb",
				borderLeftColor: "#cbd5e1",
				borderLeftWidth: 6,
				borderLeftStyle: "solid",
				marginTop: 10,
				marginRight: 0,
				marginBottom: 12,
				marginLeft: 0,
				paddingTop: 10,
				paddingRight: 12,
				paddingBottom: 10,
				paddingLeft: 14
			},
			link: {
				color: "#3478f5",
				fontWeight: "bold"
			}
		}
	},
	{
		key: "clinicalBlue",
		label: "Clinical Blue",
		styles: {
			heading: {
				color: "#0f172a",
				fontWeight: "700",
				marginBottom: 12
			},
			blockquote: {
				backgroundColor: "#eff6ff",
				borderLeftColor: "#2563eb",
				borderLeftWidth: 6,
				borderLeftStyle: "solid",
				marginTop: 12,
				marginBottom: 14,
				paddingTop: 12,
				paddingRight: 14,
				paddingBottom: 12,
				paddingLeft: 16
			},
			link: {
				color: "#1d4ed8",
				fontWeight: "700",
				textDecorationLine: "underline"
			}
		}
	},
	{
		key: "warningAmber",
		label: "Warning Amber",
		styles: {
			heading: {
				color: "#111827",
				fontWeight: "700"
			},
			blockquote: {
				backgroundColor: "#fffbeb",
				borderLeftColor: "#f59e0b",
				borderLeftWidth: 6,
				borderLeftStyle: "solid",
				marginTop: 12,
				marginBottom: 14,
				paddingTop: 12,
				paddingRight: 14,
				paddingBottom: 12,
				paddingLeft: 16
			},
			strong: {
				color: "#92400e"
			}
		}
	},
	{
		key: "denseDocument",
		label: "Dense Document",
		styles: {
			paragraph: {
				marginTop: 2,
				marginBottom: 4
			},
			heading: {
				marginTop: 4,
				marginBottom: 6
			},
			heading1: {
				fontSize: 26
			},
			heading2: {
				fontSize: 22
			},
			heading3: {
				fontSize: 18
			},
			blockquote: {
				backgroundColor: "#f8fafc",
				borderLeftColor: "#94a3b8",
				borderLeftWidth: 4,
				borderLeftStyle: "solid",
				marginTop: 8,
				marginBottom: 8,
				paddingTop: 8,
				paddingRight: 10,
				paddingBottom: 8,
				paddingLeft: 12
			},
			listItem: {
				marginBottom: 2
			}
		}
	},
	{
		key: "plainMinimal",
		label: "Plain Minimal",
		styles: {
			blockquote: {
				backgroundColor: "transparent",
				borderLeftColor: "#d1d5db",
				borderLeftWidth: 4,
				borderLeftStyle: "solid",
				marginTop: 8,
				marginBottom: 8,
				paddingTop: 4,
				paddingRight: 8,
				paddingBottom: 4,
				paddingLeft: 12
			},
			link: {
				color: "#2563eb",
				textDecorationLine: "underline"
			}
		}
	}
];

const DEFAULT_MARKDOWN_PREVIEW_STYLES = {
	root: {
		color: "#111827",
		fontFamily: "Arial, sans-serif",
		fontSize: 16,
		lineHeight: "22px"
	},
	heading: {
		color: "#111827",
		fontWeight: "700",
		marginTop: 0,
		marginBottom: 10
	},
	heading2: {
		fontSize: 24,
		lineHeight: "30px"
	},
	paragraph: {
		marginTop: 5,
		marginBottom: 5
	},
	blockquote: {
		backgroundColor: "#f6f8fb",
		borderLeftColor: "#cbd5e1",
		borderLeftWidth: 6,
		borderLeftStyle: "solid",
		marginTop: 10,
		marginRight: 0,
		marginBottom: 12,
		marginLeft: 0,
		paddingTop: 10,
		paddingRight: 12,
		paddingBottom: 10,
		paddingLeft: 14
	},
	list: {
		marginTop: 6,
		marginBottom: 8,
		paddingLeft: 24
	},
	listItem: {
		marginBottom: 5
	},
	link: {
		color: "#3478f5",
		fontWeight: "bold"
	},
	inlineCode: {
		backgroundColor: "#f5f5f5",
		borderColor: "#cccccc",
		borderWidth: 1,
		borderStyle: "solid",
		borderRadius: 3,
		color: "#111827",
		fontFamily: "Courier, monospace",
		fontWeight: "bold",
		padding: 3
	},
	codeBlock: {
		backgroundColor: "#f5f5f5",
		borderColor: "#cccccc",
		borderWidth: 1,
		borderStyle: "solid",
		borderRadius: 4,
		color: "#111827",
		fontFamily: "Courier, monospace",
		fontSize: 13,
		padding: 10
	},
	table: {
		borderColor: "#111827",
		borderWidth: 1,
		borderStyle: "solid",
		borderCollapse: "collapse"
	},
	tableHeaderCell: {
		backgroundColor: "#eeeeee",
		borderColor: "#111827",
		borderWidth: 1,
		borderStyle: "solid",
		fontWeight: "700",
		padding: 6
	},
	tableRowCell: {
		borderColor: "#111827",
		borderWidth: 1,
		borderStyle: "solid",
		padding: 6
	}
};

const normalizeSelectValue = (value, fallback = "") => {
	return _.isUndefined(value) || value === null || value === "" ? fallback : value;
};

const normalizeColorPickerValue = (color) => {
	if (!color) {
		return "";
	}
	if (typeof color === "string") {
		return color;
	}
	if (color.hex) {
		return color.hex;
	}
	if (color.rgb) {
		const { r, g, b, a } = color.rgb;
		if (!_.isUndefined(a) && a !== 1) {
			return `rgba(${r},${g},${b},${a})`;
		}
		return `rgb(${r},${g},${b})`;
	}
	return "";
};

const normalizeNumberInputValue = (value) => {
	return value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
};

const ensureStylePathParent = (nextStyles = {}, stylePath = []) => {
	if (!_.isArray(stylePath) || stylePath.length <= 1) {
		return;
	}
	let currentNode = nextStyles;
	stylePath.slice(0, -1).forEach((segment) => {
		if (!_.isPlainObject(currentNode[segment])) {
			currentNode[segment] = {};
		}
		currentNode = currentNode[segment];
	});
};

const stripManagedStylePaths = (nextStyles = {}) => {
	const cleanedStyles = _.cloneDeep(nextStyles) || {};
	MARKDOWN_STYLE_CLEAR_PATHS.forEach((stylePath) => _.unset(cleanedStyles, stylePath));
	return cleanedStyles;
};

const getControlValue = (styles = {}, stylePath = [], fallbackPath = null) => {
	const value = _.get(styles, stylePath);
	if (!_.isUndefined(value)) {
		return value;
	}
	return fallbackPath ? _.get(styles, fallbackPath, "") : "";
};

const getMergedPreviewStyle = (localStyles = {}, styleKeys = [], baseStyle = {}) => {
	const mergedStyle = _.assign({}, baseStyle);
	styleKeys.forEach((styleKey) => {
		_.assign(mergedStyle, _.get(DEFAULT_MARKDOWN_PREVIEW_STYLES, styleKey, {}), _.get(localStyles, styleKey, {}));
	});
	if (styleKeys.includes("blockquote")) {
		if (mergedStyle.borderStyle && !mergedStyle.borderLeftStyle) {
			mergedStyle.borderLeftStyle = mergedStyle.borderStyle;
		}
		delete mergedStyle.borderStyle;
	}
	return mergedStyle;
};

const cardStyle = {
	padding: "12px",
	borderRadius: "10px",
	backgroundColor: "#ffffff",
	border: "1px solid #e5e7eb"
};

const sectionTitleStyle = {
	fontSize: "12px",
	fontWeight: 700,
	color: "#475569",
	textTransform: "uppercase",
	marginBottom: "10px"
};

const controlGridStyle = {
	display: "grid",
	gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
	gap: "10px",
	marginBottom: "12px"
};

const MarkdownStyleQuickEditor = ({
	titleLabel = "Markdown Style",
	description = "These controls write into Stylesheet JSON under markdown.",
	introText = "Tune markdown typography, callouts, lists, code, and tables for item labels rendered by the players.",
	previewText = "Preview reflects local Markdown style overrides authored at this level. Inherited styles still apply at runtime when you leave fields blank here.",
	stylesValue = "",
	onChangeStyles,
	readOnly = false,
	invalidValueMessage = "Fix invalid Markdown Styles JSON before using these quick controls."
}) => {
	const markdownStyleObject = parseJsonStyleObject(stylesValue);
	const markdownStyleInvalid = markdownStyleObject === null;
	const controlsDisabled = readOnly || markdownStyleInvalid;
	const localMarkdownStyle = markdownStyleInvalid ? {} : markdownStyleObject;
	const [selectedTemplateKey, setSelectedTemplateKey] = React.useState("");
	const [showJsonPreview, setShowJsonPreview] = React.useState(false);
	const safeIdPrefix = titleLabel.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "markdown-style";

	const commitStylesObject = (nextStyles = {}) => {
		if (typeof onChangeStyles === "function") {
			onChangeStyles(stringifyJsonStyleObject(nextStyles));
		}
	};

	const updateStyleValue = (stylePath = [], nextValue = "") => {
		const nextStyles = parseJsonStyleObject(stylesValue);
		if (nextStyles === null) {
			return;
		}
		ensureStylePathParent(nextStyles, stylePath);
		if (nextValue === "" || _.isUndefined(nextValue) || nextValue === null) {
			_.unset(nextStyles, stylePath);
		} else {
			_.set(nextStyles, stylePath, nextValue);
		}
		commitStylesObject(nextStyles);
	};

	const updateMirroredStyleValue = (stylePaths = [], nextValue = "") => {
		const nextStyles = parseJsonStyleObject(stylesValue);
		if (nextStyles === null) {
			return;
		}
		stylePaths.forEach((stylePath) => {
			ensureStylePathParent(nextStyles, stylePath);
			if (nextValue === "" || _.isUndefined(nextValue) || nextValue === null) {
				_.unset(nextStyles, stylePath);
			} else {
				_.set(nextStyles, stylePath, nextValue);
			}
		});
		commitStylesObject(nextStyles);
	};

	const applyTemplate = (templateKey = "") => {
		const template = MARKDOWN_STYLE_TEMPLATE_PRESETS.find((entry) => entry.key === templateKey);
		const nextStyles = parseJsonStyleObject(stylesValue);
		if (!template || nextStyles === null) {
			return;
		}
		const clearedStyles = stripManagedStylePaths(nextStyles);
		commitStylesObject(_.merge({}, clearedStyles, _.cloneDeep(template.styles)));
	};

	const clearOverrides = () => {
		commitStylesObject({});
	};

	const renderNumberControl = (label, stylePath, fallbackPath = null) => (
		<div>
			<label>{label}</label>
			<FormControl
				disabled={controlsDisabled}
				type="number"
				min="0"
				step="1"
				value={normalizeSelectValue(getControlValue(localMarkdownStyle, stylePath, fallbackPath), "")}
				onChange={(e) => updateStyleValue(stylePath, normalizeNumberInputValue(e.target.value))}
			/>
		</div>
	);

	const renderMirroredNumberControl = (label, stylePaths) => (
		<div>
			<label>{label}</label>
			<FormControl
				disabled={controlsDisabled}
				type="number"
				min="0"
				step="1"
				value={normalizeSelectValue(getControlValue(localMarkdownStyle, stylePaths[0], stylePaths[1]), "")}
				onChange={(e) => updateMirroredStyleValue(stylePaths, normalizeNumberInputValue(e.target.value))}
			/>
		</div>
	);

	const renderTextControl = (label, stylePath, placeholder = "Inherit") => (
		<div>
			<label>{label}</label>
			<FormControl
				disabled={controlsDisabled}
				type="text"
				value={normalizeSelectValue(_.get(localMarkdownStyle, stylePath), "")}
				placeholder={placeholder}
				onChange={(e) => updateStyleValue(stylePath, e.target.value)}
			/>
		</div>
	);

	const renderSelectControl = (label, stylePath, options) => (
		<div>
			<label>{label}</label>
			<FormControl
				disabled={controlsDisabled}
				componentClass="select"
				value={normalizeSelectValue(_.get(localMarkdownStyle, stylePath), "")}
				onChange={(e) => updateStyleValue(stylePath, e.target.value)}
			>
				{options.map((option) => (
					<option key={`${stylePath.join("-")}-${option.label}`} value={option.value}>{option.label}</option>
				))}
			</FormControl>
		</div>
	);

	const renderColorControl = (label, stylePath, title = label) => (
		<div style={{ marginBottom: "12px" }}>
			<label>{label}</label>
			<div>
				<ColorPicker
					id={`popover-${safeIdPrefix}-${stylePath.join("-")}`}
					title={title}
					onChangeColor={(color) => updateStyleValue(stylePath, normalizeColorPickerValue(color))}
					onClearColor={() => updateStyleValue(stylePath, "")}
					presetColors={globals.defaultColors}
					color={normalizeSelectValue(_.get(localMarkdownStyle, stylePath), "")}
				/>
			</div>
			<div style={{ marginTop: "4px", fontSize: "11px", color: "#6b7280" }}>{normalizeSelectValue(_.get(localMarkdownStyle, stylePath), "(inherit)")}</div>
		</div>
	);

	const renderMirroredColorControl = (label, stylePaths, title = label) => (
		<div style={{ marginBottom: "12px" }}>
			<label>{label}</label>
			<div>
				<ColorPicker
					id={`popover-${safeIdPrefix}-${stylePaths[0].join("-")}`}
					title={title}
					onChangeColor={(color) => updateMirroredStyleValue(stylePaths, normalizeColorPickerValue(color))}
					onClearColor={() => updateMirroredStyleValue(stylePaths, "")}
					presetColors={globals.defaultColors}
					color={normalizeSelectValue(getControlValue(localMarkdownStyle, stylePaths[0], stylePaths[1]), "")}
				/>
			</div>
			<div style={{ marginTop: "4px", fontSize: "11px", color: "#6b7280" }}>{normalizeSelectValue(getControlValue(localMarkdownStyle, stylePaths[0], stylePaths[1]), "(inherit)")}</div>
		</div>
	);

	const previewRootStyle = getMergedPreviewStyle(localMarkdownStyle, ["root", "text"]);
	const previewHeadingStyle = getMergedPreviewStyle(localMarkdownStyle, ["heading", "heading2"]);
	const previewParagraphStyle = getMergedPreviewStyle(localMarkdownStyle, ["paragraph"]);
	const previewBlockquoteStyle = getMergedPreviewStyle(localMarkdownStyle, ["blockquote"]);
	const previewListStyle = getMergedPreviewStyle(localMarkdownStyle, ["list", "listUnordered"]);
	const previewListItemStyle = getMergedPreviewStyle(localMarkdownStyle, ["listItem", "listUnorderedItemText"]);
	const previewLinkStyle = getMergedPreviewStyle(localMarkdownStyle, ["link"]);
	const previewInlineCodeStyle = getMergedPreviewStyle(localMarkdownStyle, ["codeInline", "inlineCode"]);
	const previewCodeBlockStyle = getMergedPreviewStyle(localMarkdownStyle, ["codeBlock"]);
	const previewTableStyle = getMergedPreviewStyle(localMarkdownStyle, ["table"]);
	const previewTableHeaderCellStyle = getMergedPreviewStyle(localMarkdownStyle, ["tableHeaderCell"]);
	const previewTableRowCellStyle = getMergedPreviewStyle(localMarkdownStyle, ["tableRowCell"]);

	return (
		<div style={{ marginBottom: "16px", padding: "14px", border: "1px solid #e5e7eb", borderRadius: "12px", backgroundColor: "#fafafa" }}>
			<AfHelp description={description}>
				<label>{titleLabel}</label>
			</AfHelp>
			<div style={{ marginTop: "6px", marginBottom: "12px", fontSize: "12px", color: "#6b7280" }}>
				{introText}
			</div>
			{markdownStyleInvalid &&
				<div style={{ marginBottom: "12px", padding: "10px 12px", borderRadius: "8px", backgroundColor: "#fef2f2", color: "#991b1b", fontSize: "12px" }}>
					{invalidValueMessage}
				</div>
			}
			<div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-end", marginBottom: "14px" }}>
				<div style={{ minWidth: "240px", flex: "1 1 280px" }}>
					<label>Template</label>
					<FormControl
						disabled={controlsDisabled}
						componentClass="select"
						value={selectedTemplateKey}
						onChange={(e) => {
							const nextTemplateKey = e.target.value;
							setSelectedTemplateKey("");
							if (nextTemplateKey) {
								applyTemplate(nextTemplateKey);
							}
						}}
					>
						<option value="">Choose Template</option>
						{MARKDOWN_STYLE_TEMPLATE_PRESETS.map((template) => (
							<option key={template.key} value={template.key}>{template.label}</option>
						))}
					</FormControl>
				</div>
				<Button bsSize="small" disabled={controlsDisabled} onClick={clearOverrides}>Clear Local Overrides</Button>
			</div>
			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "12px", marginBottom: "14px" }}>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Text</div>
					{renderColorControl("Text Color", ["text", "color"], "Markdown Text Color")}
					<div style={controlGridStyle}>
						{renderNumberControl("Font Size", ["text", "fontSize"])}
						{renderNumberControl("Line Height", ["text", "lineHeight"])}
					</div>
					<div style={controlGridStyle}>
						{renderSelectControl("Font Weight", ["text", "fontWeight"], FONT_WEIGHT_OPTIONS)}
						{renderSelectControl("Text Align", ["text", "textAlign"], TEXT_ALIGN_OPTIONS)}
					</div>
					<div style={{ marginBottom: "12px" }}>
						{renderTextControl("Font Family", ["text", "fontFamily"])}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("Paragraph Top", ["paragraph", "marginTop"])}
						{renderNumberControl("Paragraph Bottom", ["paragraph", "marginBottom"])}
					</div>
					{renderColorControl("Link Color", ["link", "color"], "Markdown Link Color")}
					<div style={controlGridStyle}>
						{renderSelectControl("Link Weight", ["link", "fontWeight"], FONT_WEIGHT_OPTIONS)}
						{renderSelectControl("Link Decoration", ["link", "textDecorationLine"], TEXT_DECORATION_OPTIONS)}
					</div>
				</div>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Headings</div>
					{renderColorControl("Heading Color", ["heading", "color"], "Markdown Heading Color")}
					<div style={controlGridStyle}>
						{renderSelectControl("Heading Weight", ["heading", "fontWeight"], FONT_WEIGHT_OPTIONS)}
						{renderSelectControl("Heading Align", ["heading", "textAlign"], TEXT_ALIGN_OPTIONS)}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("Heading Top", ["heading", "marginTop"])}
						{renderNumberControl("Heading Bottom", ["heading", "marginBottom"])}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("H1 Size", ["heading1", "fontSize"])}
						{renderNumberControl("H2 Size", ["heading2", "fontSize"])}
						{renderNumberControl("H3 Size", ["heading3", "fontSize"])}
					</div>
				</div>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Blockquote</div>
					{renderColorControl("Background", ["blockquote", "backgroundColor"], "Markdown Blockquote Background")}
					{renderColorControl("Left Border", ["blockquote", "borderLeftColor"], "Markdown Blockquote Left Border")}
					<div style={controlGridStyle}>
						{renderNumberControl("Border Width", ["blockquote", "borderLeftWidth"])}
						{renderSelectControl("Left Border Style", ["blockquote", "borderLeftStyle"], BORDER_STYLE_OPTIONS)}
						{renderNumberControl("Border Radius", ["blockquote", "borderRadius"])}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("Padding Top", ["blockquote", "paddingTop"])}
						{renderNumberControl("Padding Right", ["blockquote", "paddingRight"])}
						{renderNumberControl("Padding Bottom", ["blockquote", "paddingBottom"])}
						{renderNumberControl("Padding Left", ["blockquote", "paddingLeft"])}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("Margin Top", ["blockquote", "marginTop"])}
						{renderNumberControl("Margin Bottom", ["blockquote", "marginBottom"])}
					</div>
				</div>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Lists</div>
					<div style={controlGridStyle}>
						{renderNumberControl("List Top", ["list", "marginTop"])}
						{renderNumberControl("List Bottom", ["list", "marginBottom"])}
						{renderNumberControl("List Indent", ["list", "paddingLeft"])}
					</div>
					<div style={controlGridStyle}>
						{renderNumberControl("Item Bottom", ["listItem", "marginBottom"])}
						{renderMirroredNumberControl("Item Size", [["listItem", "fontSize"], ["listUnorderedItemText", "fontSize"], ["listOrderedItemText", "fontSize"]])}
						{renderMirroredNumberControl("Item Line Height", [["listItem", "lineHeight"], ["listUnorderedItemText", "lineHeight"], ["listOrderedItemText", "lineHeight"]])}
					</div>
					{renderMirroredColorControl("Item Text Color", [["listItem", "color"], ["listUnorderedItemText", "color"], ["listOrderedItemText", "color"]], "Markdown List Item Text Color")}
					<div style={controlGridStyle}>
						{renderNumberControl("Bullet Size", ["listUnorderedItemIcon", "fontSize"])}
						{renderSelectControl("Bullet Weight", ["listUnorderedItemIcon", "fontWeight"], FONT_WEIGHT_OPTIONS)}
					</div>
					{renderColorControl("Bullet Color", ["listUnorderedItemIcon", "color"], "Markdown List Bullet Color")}
				</div>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Code</div>
					{renderMirroredColorControl("Inline Background", [["inlineCode", "backgroundColor"], ["codeInline", "backgroundColor"]], "Inline Code Background")}
					{renderMirroredColorControl("Inline Text", [["inlineCode", "color"], ["codeInline", "color"]], "Inline Code Text Color")}
					<div style={controlGridStyle}>
						{renderMirroredNumberControl("Inline Padding", [["inlineCode", "padding"], ["codeInline", "padding"]])}
						{renderMirroredNumberControl("Inline Radius", [["inlineCode", "borderRadius"], ["codeInline", "borderRadius"]])}
						{renderMirroredNumberControl("Inline Border", [["inlineCode", "borderWidth"], ["codeInline", "borderWidth"]])}
					</div>
					{renderColorControl("Block Background", ["codeBlock", "backgroundColor"], "Code Block Background")}
					{renderColorControl("Block Text", ["codeBlock", "color"], "Code Block Text Color")}
					<div style={controlGridStyle}>
						{renderNumberControl("Block Padding", ["codeBlock", "padding"])}
						{renderNumberControl("Block Radius", ["codeBlock", "borderRadius"])}
						{renderNumberControl("Block Size", ["codeBlock", "fontSize"])}
					</div>
				</div>
				<div style={cardStyle}>
					<div style={sectionTitleStyle}>Tables</div>
					{renderColorControl("Table Border", ["table", "borderColor"], "Markdown Table Border")}
					<div style={controlGridStyle}>
						{renderNumberControl("Table Border Width", ["table", "borderWidth"])}
						{renderNumberControl("Table Radius", ["table", "borderRadius"])}
					</div>
					{renderColorControl("Header Background", ["tableHeaderCell", "backgroundColor"], "Markdown Table Header Background")}
					{renderColorControl("Header Text", ["tableHeaderCell", "color"], "Markdown Table Header Text Color")}
					<div style={controlGridStyle}>
						{renderNumberControl("Header Padding", ["tableHeaderCell", "padding"])}
						{renderNumberControl("Cell Padding", ["tableRowCell", "padding"])}
					</div>
				</div>
			</div>
			<div style={{ padding: "12px", borderRadius: "10px", backgroundColor: "#ffffff", border: "1px solid #e5e7eb" }}>
				<div style={sectionTitleStyle}>Preview</div>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px" }}>
					<div style={{ flex: "1 1 320px", fontSize: "12px", color: "#6b7280" }}>{previewText}</div>
					<Button bsSize="small" onClick={() => setShowJsonPreview(!showJsonPreview)}>
						{showJsonPreview ? "Hide Markdown JSON" : "Show Markdown JSON"}
					</Button>
				</div>
				<div style={{ padding: "16px", borderRadius: "12px", border: "1px solid #dbe4f0", backgroundColor: "#ffffff" }}>
					<div style={previewRootStyle}>
						<h2 style={previewHeadingStyle}>Change Week Topic</h2>
						<p style={previewParagraphStyle}>
							Use markdown for <strong>structured instructions</strong>, links, lists, callouts, and inline <code style={previewInlineCodeStyle}>codes</code>.
						</p>
						<blockquote style={previewBlockquoteStyle}>
							<p style={{ ...previewParagraphStyle, marginTop: 0 }}>
								<strong>New Way</strong>
							</p>
							<p style={{ ...previewParagraphStyle, marginBottom: 0 }}>
								Beginning 06/16/26, the FPOC Screening tool will include a pop-up with a link to the CDC.
							</p>
						</blockquote>
						<ul style={previewListStyle}>
							<li style={previewListItemStyle}>Bullets inherit list spacing and text styles.</li>
							<li style={previewListItemStyle}>Links can use a dedicated <a href="#markdown-preview" onClick={(event) => event.preventDefault()} style={previewLinkStyle}>markdown link style</a>.</li>
						</ul>
						<pre style={previewCodeBlockStyle}>status = "ready"</pre>
						<table style={previewTableStyle}>
							<thead>
								<tr>
									<th style={previewTableHeaderCellStyle}>Field</th>
									<th style={previewTableHeaderCellStyle}>Value</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td style={previewTableRowCellStyle}>Audience</td>
									<td style={previewTableRowCellStyle}>RNs</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				{showJsonPreview &&
					<pre style={{ marginTop: "10px", marginBottom: 0, maxHeight: "260px", overflow: "auto", backgroundColor: "#0f172a", color: "#e5e7eb", borderRadius: "6px", padding: "10px", fontSize: "12px" }}>{JSON.stringify(localMarkdownStyle, null, 2)}</pre>
				}
			</div>
		</div>
	);
};

export default MarkdownStyleQuickEditor;
